import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "./index.css";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import Home from "./pages/Home";
import Fleet from "./pages/Fleet";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import BookRide from "./pages/BookRide";
import BookRental from "./pages/BookRental";
import BookingConfirmation from "./pages/BookingConfirmation";
import Bookings from "./pages/Bookings";
import BookingDetail from "./pages/BookingDetail";
import Profile from "./pages/Profile";
import Support from "./pages/Support";
import PaymentReturn from "./pages/PaymentReturn";
import DepositPage from "./pages/DepositPage";
import FAQPage from "./pages/FAQ";
import BackOffice from "./pages/BackOffice";
import ManagerLogin from "./pages/ManagerLogin";
import MobileApp from "./mobile/MobileApp";
import { useIsMobile } from "./mobile/useIsMobile";
import DriverRentals from "./pages/DriverRentals";
import DriverRentalsDashboard from "./pages/DriverRentalsDashboard";
import VehicleDetail from "./pages/VehicleDetail";
import EventDetail from "./pages/EventDetail";
import StaffRoute from "./components/StaffRoute";
import ConciergeChat from "./components/ConciergeChat";
import ManagerAcceptInvite from "./pages/ManagerAcceptInvite";
import BookingShare from "./pages/BookingShare";
import FindBooking from "./pages/FindBooking";
import SpecialPromo from "./pages/SpecialPromo";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import ContractSign from "./pages/ContractSign";
import useVisitTracker from "./hooks/useVisitTracker";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-ink-900 text-white">
      <Navbar />
      <main className="pt-24 flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AppRouter() {
  // Handle Emergent Auth callback (session_id in URL fragment) FIRST
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <RouterBody />;
}

function RouterBody() {
  const isMobile = useIsMobile();
  const location = useLocation();
  useVisitTracker();
  // Never hijack auth callback, manager login, or /backoffice routes on mobile.
  const isProtected = location.pathname.startsWith("/backoffice")
    || location.pathname.startsWith("/manager-login")
    || location.pathname.startsWith("/manager/accept")
    || location.pathname.startsWith("/auth/callback");
  if (isMobile && !isProtected) {
    return <MobileApp />;
  }
  const routes = (
    <Shell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/vehicles/:vehicleId" element={<VehicleDetail />} />
        <Route path="/events/:eventId" element={<EventDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/manager-login" element={<ManagerLogin />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/manager/accept/:token" element={<ManagerAcceptInvite />} />
        <Route path="/b/:bookingId" element={<BookingShare />} />
        <Route path="/booking/confirmed/:bookingId" element={<BookingConfirmation />} />
        <Route path="/find" element={<FindBooking />} />
        <Route path="/support" element={<Support />} />
        <Route path="/contract/sign/:bookingId" element={<ContractSign />} />
        <Route path="/payment-return" element={<PaymentReturn />} />
        <Route path="/pay/:bookingId" element={<DepositPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/promo/maybach-special" element={<Navigate to="/promo/maybach250" replace />} />
        <Route path="/promo/:promoCode" element={<SpecialPromo />} />
        <Route path="/girls-night" element={<Navigate to="/" replace />} />
        <Route path="/driver-rentals" element={<DriverRentals />} />
        <Route path="/driver-rentals/dashboard" element={
          <ProtectedRoute><DriverRentalsDashboard /></ProtectedRoute>
        } />
        <Route path="/book/ride/:vehicleId" element={<BookRide />} />
        <Route path="/book/rental/:vehicleId" element={<BookRental />} />
        <Route path="/bookings" element={
          <ProtectedRoute><Bookings /></ProtectedRoute>
        } />
        <Route path="/bookings/:bookingId" element={
          <ProtectedRoute><BookingDetail /></ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute><Profile /></ProtectedRoute>
        } />
        <Route path="/backoffice" element={
          <StaffRoute><BackOffice /></StaffRoute>
        } />
      </Routes>
      <ConciergeChat />
    </Shell>
  );
  return routes;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster theme="dark" position="top-right"
          toastOptions={{
            style: {
              background: "#0B0B0C",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.08)",
            },
          }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
